import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../../features/auth/services/auth.service';
import { AuthInitializerService } from '../../features/auth/services/auth-initializer.service';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserStateService } from '../services/user-state.service';

/**
 * Dashboard and shared shell: admin, org contributor, or manufacturer user.
 */
export const authorizedUserGuard: CanActivateFn = (route, state) => {
  const showDebugInfo = environment.showDebugInfo;
  
  if (showDebugInfo) {
    console.log('Authorized User guard activated for route:', state.url);
  }
  
  const router = inject(Router);
  const authService = inject(AuthService);
  const userStateService = inject(UserStateService);
  const authInitializer = inject(AuthInitializerService);
  
  if (!authInitializer.isAuthReady()) {
    if (showDebugInfo) {
      console.log('Authorized User guard: Auth not ready, waiting for initialization');
    }
    return of(authInitializer.initializeAuth()).pipe(
      switchMap(() => checkAuthorizedUserAccess(authService, userStateService, router, showDebugInfo))
    );
  }
  
  return checkAuthorizedUserAccess(authService, userStateService, router, showDebugInfo);
};

function checkAuthorizedUserAccess(
  authService: AuthService, 
  userStateService: UserStateService, 
  router: Router, 
  showDebugInfo: boolean
): Observable<boolean> {
  return authService.checkAuthenticated().pipe(
    take(1),
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        if (showDebugInfo) {
          console.log('Authorized User guard: User not authenticated, redirecting to login');
        }
        router.navigate(['/login']);
        return of(false);
      }
      
      return userStateService.state$.pipe(
        take(1),
        map(state => {
          const hasAccess =
            state.isAdmin || !!state.organisationId || !!state.manufacturerId;
          
          if (showDebugInfo) {
            console.log('Authorized User guard check result:', {
              isAdmin: state.isAdmin,
              organisationId: state.organisationId,
              manufacturerId: state.manufacturerId,
              hasAccess
            });
          }
          
          if (hasAccess) {
            return true;
          }
          
          router.navigate(['/unauthorized']);
          return false;
        })
      );
    })
  );
}
